import { useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle, AlertCircle, CheckCircle2, HelpCircle, Loader2,
} from 'lucide-react';
import { useVatLang } from './VatLang';
import { vatReportingApi } from '../../services/vatReportingApi';
import { EntryDrillModal } from './EntryDrillModal';
import { AnnualStatusBar } from './AnnualStatusBar';
import { ElsterEntryHelp } from './ElsterEntryHelp';
import { LatePostingsPanel, YearBoundaryPanel, type BoundaryDrill } from './YearBridgePanels';
import type {
    AnnualLineValue, AnnualMatrixRow, AnnualReturnResponse, VatCheck, VatCheckStatus, VatCompany,
} from '../../types/vatReporting';

/**
 * The Jahresmeldung tab - the German annual VAT return and the reconciliation behind it.
 *
 * Everything over one (company, year), out of ONE fetch, because the pieces compare each other:
 * the status ladder, the controls, the twelve-month matrix per Kennzahl (filed / computed /
 * difference), the annual form lines under the year's own rounding rules, the ELSTER entry help,
 * and the two lists that say whether the year's total is still the year's total - what a person
 * carried across December/January, and what the ERP moved after a month was filed.
 *
 * TWO KINDS OF TABLE, ON PURPOSE. The matrix, the form panel and the Erfassungshilfe are pivots
 * in the FORM's own order: reordering their rows or dragging their columns would only un-align
 * them from the paper they mirror, so they skip the sortable/resizable standard and say so. The
 * bridge panels are work lists and follow the standard in full, as does every EntryDrillModal
 * they open.
 */

interface Props {
    company: VatCompany;
    year: number;
    canEdit: boolean;
    onError: (message: string) => void;
}

type MatrixView = 'filed' | 'computed' | 'diff';

const money = (v: number | null | undefined, dp = 2) =>
    v === null || v === undefined
        ? '—'
        : new Intl.NumberFormat('de-CH', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);

const whole = (v: number | null | undefined) =>
    v === null || v === undefined ? '—' : new Intl.NumberFormat('de-CH').format(v);

/** Bases print as whole euros, tax amounts to the cent - same rule as the monthly table. */
const figure = (v: number | null, isTax: boolean) => (isTax ? money(v) : whole(v));

const CHECK_ICON: Record<VatCheckStatus, JSX.Element> = {
    ok: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    error: <AlertCircle className="h-4 w-4 text-red-600" />,
    unavailable: <HelpCircle className="h-4 w-4 text-gray-400" />,
};

export function AnnualDeTab({ company, year, canEdit, onError }: Props) {
    const { t, lang } = useVatLang();
    const [data, setData] = useState<AnnualReturnResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<MatrixView>('filed');
    const [drill, setDrill] = useState<{ month: number; kennzahl: string; label: string } | null>(null);
    /** A single posting, in whatever month holds it - the bridge panels drill by entry number. */
    const [entryDrill, setEntryDrill] = useState<BoundaryDrill | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        vatReportingApi.getAnnual({ company, year })
            .then(setData)
            .catch(e => onError(e instanceof Error ? e.message : t('panel.loadFailedReturn')))
            .finally(() => setLoading(false));
    }, [company, year, onError]);

    useEffect(load, [load]);

    if (loading && !data) {
        return (
            <div className="p-12 text-center text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />{t('page.loading')}
            </div>
        );
    }
    if (!data) return null;

    const noReportingCurrency = data.reportingCurrency === null;
    const monthNames = monthShortNames(lang);

    return (
        <div className="space-y-4">
            {/* Keyed on what was stored: the filing form seeds its inputs once, from the record
                that existed when it mounted. Without the key, filing and reopening in one
                sitting would leave the second form holding the first one's figures. */}
            <AnnualStatusBar key={`${data.annualReturn?.status ?? 'open'}-${data.annualReturn?.filedAt ?? ''}`}
                             data={data} company={company} year={year}
                             canEdit={canEdit} onChanged={load} onError={onError} />

            {noReportingCurrency && (
                <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                        <strong>{company.toUpperCase()}</strong>{' '}
                        {t('check.REPORTING_CURRENCY.unavailable')}
                    </div>
                </div>
            )}

            <AnnualChecksPanel checks={data.checks} />

            <MatrixPanel data={data} view={view} setView={setView} monthNames={monthNames}
                         onDrillCell={(month, kennzahl, label) => setDrill({ month, kennzahl, label })} />

            {!noReportingCurrency && <AnnualFormPanel data={data} />}

            {!noReportingCurrency && <ElsterEntryHelp data={data} />}

            <YearBoundaryPanel boundary={data.yearBoundary} year={year} onDrill={setEntryDrill} />

            <LatePostingsPanel late={data.latePostings} year={year} monthNames={monthNames}
                               onDrill={setEntryDrill} />

            {drill && (
                <EntryDrillModal isOpen onClose={() => setDrill(null)}
                                 company={company} year={year} month={drill.month}
                                 title={`Kz ${drill.kennzahl} — ${monthNames[drill.month - 1]} ${year}`}
                                 subtitle={drill.label}
                                 filter={{ kennzahl: drill.kennzahl }}
                                 canEdit={canEdit}
                                 onChanged={load} />
            )}

            {entryDrill && (
                <EntryDrillModal isOpen onClose={() => setEntryDrill(null)}
                                 company={company}
                                 year={entryDrill.year} month={entryDrill.month}
                                 title={entryDrill.title}
                                 subtitle={entryDrill.subtitle}
                                 filter={{
                                     entryNos: entryDrill.entryNos,
                                     includeExcluded: entryDrill.includeExcluded,
                                     includeSettlement: true,
                                 }}
                                 canEdit={canEdit}
                                 onChanged={load} />
            )}
        </div>
    );
}

function monthShortNames(lang: string): string[] {
    const fmt = new Intl.DateTimeFormat(lang === 'de' ? 'de-CH' : 'en-GB', { month: 'short' });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2026, i, 15)));
}

// ---------------------------------------------------------------------------- checks ----

function AnnualChecksPanel({ checks }: { checks: VatCheck[] }) {
    const { t, tOr } = useVatLang();
    const [showAll, setShowAll] = useState(false);
    const problems = checks.filter(c => c.status !== 'ok');
    const shown = showAll ? checks : problems;

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('controls.title')}
                    <span className="ml-2 font-normal text-gray-500">
                        {problems.length === 0
                            ? t('controls.allPassed', { count: checks.length })
                            : t('controls.needAttention', { problems: problems.length, count: checks.length })}
                    </span>
                </h3>
                <button onClick={() => setShowAll(v => !v)}
                        className="text-xs text-blue-600 hover:text-blue-800">
                    {showAll ? t('controls.showFindings') : t('controls.showAll')}
                </button>
            </div>
            {shown.length === 0 ? (
                <div className="px-4 py-6 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('controls.everyPassed')}
                </div>
            ) : (
                <ul className="divide-y divide-gray-100">
                    {shown.map(c => (
                        <li key={c.code} className="px-4 py-2.5 flex items-start gap-3">
                            <span className="mt-0.5 shrink-0">{CHECK_ICON[c.status]}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                    {tOr(`checkTitle.${c.code}`, c.title, c.params)}
                                </div>
                                <div className="text-sm text-gray-600">
                                    {tOr(c.messageKey ?? `check.${c.code}.${c.status}`, c.detail, c.params)}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------- matrix ----

function MatrixPanel({ data, view, setView, monthNames, onDrillCell }: {
    data: AnnualReturnResponse;
    view: MatrixView;
    setView: (v: MatrixView) => void;
    monthNames: string[];
    onDrillCell: (month: number, kennzahl: string, label: string) => void;
}) {
    const { t } = useVatLang();
    const noComputed = data.reportingCurrency === null;

    const cell = (row: AnnualMatrixRow, i: number): { text: string; tone: string; drillable: boolean } => {
        const filed = row.filedByMonth[i];
        const computed = row.computedByMonth[i];
        const drillable = row.kennzahl !== '83' && row.kennzahl !== '39'
            && (row.entryCountByMonth[i] ?? 0) > 0;
        if (view === 'filed') {
            return { text: figure(filed, row.isTaxAmount), tone: 'text-gray-900', drillable };
        }
        if (view === 'computed') {
            return { text: figure(computed, row.isTaxAmount), tone: 'text-gray-900', drillable };
        }
        // diff: only where a filed figure exists to compare against.
        if (filed === null) return { text: '·', tone: 'text-gray-300', drillable };
        const d = Math.round(((computed ?? 0) - filed) * 100) / 100;
        if (Math.abs(d) < 0.005) return { text: '✓', tone: 'text-green-600', drillable };
        return { text: money(d), tone: 'font-medium text-amber-700', drillable };
    };

    const sum = (row: AnnualMatrixRow): string => {
        if (view === 'filed') return figure(row.filedSum, row.isTaxAmount);
        if (view === 'computed') return figure(row.computedSum, row.isTaxAmount);
        if (row.filedSum === null || row.computedSum === null) return '—';
        const d = Math.round((row.computedSum - row.filedSum) * 100) / 100;
        return Math.abs(d) < 0.005 ? '✓' : money(d);
    };

    const views: { key: MatrixView; label: string; disabled?: boolean }[] = [
        { key: 'filed', label: t('annual.viewFiled') },
        { key: 'computed', label: t('annual.viewComputed'), disabled: noComputed },
        { key: 'diff', label: t('annual.viewDiff'), disabled: noComputed },
    ];

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('annual.matrixTitle')}
                    <span className="ml-2 font-normal text-gray-500">{t('annual.matrixSubtitle')}</span>
                </h3>
                <div className="inline-flex rounded border overflow-hidden text-xs" role="group">
                    {views.map(v => (
                        <button key={v.key}
                                onClick={() => !v.disabled && setView(v.key)}
                                disabled={v.disabled}
                                aria-pressed={view === v.key}
                                className={`px-2.5 py-1.5 font-medium transition-colors ${
                                    view === v.key
                                        ? 'bg-blue-600 text-white'
                                        : v.disabled
                                            ? 'bg-gray-50 text-gray-300'
                                            : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                            {v.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs tabular-nums">
                    <thead>
                        <tr className="text-[11px] uppercase text-gray-500 border-b bg-white sticky top-0">
                            <th className="text-left px-3 py-2 whitespace-nowrap">Kz</th>
                            {monthNames.map((m, i) => {
                                const status = data.monthStatus[i];
                                return (
                                    <th key={m} className="text-right px-2 py-2 whitespace-nowrap">
                                        <span title={status?.status === 'filed'
                                            ? t('annual.monthFiled')
                                            : t('annual.monthOpen')}>
                                            {m}
                                            {status?.status !== 'filed' && (
                                                <span className="text-amber-500 ml-0.5">°</span>
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                            <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Σ</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.matrix.map(row => (
                            <tr key={row.kennzahl} className="hover:bg-gray-50">
                                <td className="px-3 py-1.5 whitespace-nowrap">
                                    <span className="font-medium text-gray-900" title={row.label}>{row.kennzahl}</span>
                                </td>
                                {row.filedByMonth.map((_, i) => {
                                    const c = cell(row, i);
                                    return (
                                        <td key={i} className={`px-2 py-1.5 text-right whitespace-nowrap ${c.tone}`}>
                                            {c.drillable ? (
                                                <button className="hover:text-blue-700 hover:underline decoration-dotted"
                                                        onClick={() => onDrillCell(i + 1, row.kennzahl, row.label)}>
                                                    {c.text}
                                                </button>
                                            ) : c.text}
                                        </td>
                                    );
                                })}
                                <td className="px-3 py-1.5 text-right font-semibold text-gray-900 whitespace-nowrap">
                                    {sum(row)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-2 border-t text-xs text-gray-500">
                {view === 'diff' ? t('annual.diffLegend') : t('annual.matrixLegend')}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------------- form panel ----

function AnnualFormPanel({ data }: { data: AnnualReturnResponse }) {
    const { t } = useVatLang();
    const isFiled = data.annualReturn?.status === 'filed';

    const rowTone = (line: AnnualLineValue) => line.kind === 'derived' ? 'bg-gray-50' : '';

    const bridge = (line: AnnualLineValue): string => {
        if (line.reportedValue === null || line.monthlyFiledSum === null) return '—';
        const d = Math.round((line.reportedValue - line.monthlyFiledSum) * 100) / 100;
        return Math.abs(d) < 0.005 ? '✓' : money(d);
    };

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('annual.formTitle', { year: data.year })}
                    <span className="ml-2 font-normal text-gray-500">{t('annual.formSubtitle')}</span>
                </h3>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm tabular-nums">
                    <thead>
                        <tr className="text-xs uppercase text-gray-500 border-b">
                            <th className="text-left px-4 py-2 whitespace-nowrap">{t('annual.colLine')}</th>
                            <th className="text-left px-4 py-2">{t('annual.colLabel')}</th>
                            <th className="text-right px-4 py-2 whitespace-nowrap">{t('annual.colRaw')}</th>
                            <th className="text-right px-4 py-2 whitespace-nowrap">{t('annual.colReported')}</th>
                            <th className="text-right px-4 py-2 whitespace-nowrap">{t('annual.colMonthlySum')}</th>
                            <th className="text-right px-4 py-2 whitespace-nowrap">{t('annual.colBridge')}</th>
                            {isFiled && (
                                <th className="text-right px-4 py-2 whitespace-nowrap">{t('annual.colFiled')}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {data.lines.map(line => {
                            const isTax = line.kind !== 'base';
                            const filedDiff = isFiled && line.filedValue !== null && line.reportedValue !== null
                                ? Math.round((line.reportedValue - line.filedValue) * 100) / 100 : null;
                            return (
                                <tr key={line.key} className={rowTone(line)}>
                                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                                        {line.formLineNo !== null ? `Z. ${line.formLineNo}` : '—'}
                                    </td>
                                    <td className="px-4 py-2 text-gray-900">
                                        {line.label}
                                        {line.kennzahl && (
                                            <span className="ml-1.5 text-xs text-gray-400">Kz {line.kennzahl}</span>
                                        )}
                                        {line.note && (
                                            <span className="ml-1.5 text-xs text-amber-700">({line.note})</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-right text-gray-500 whitespace-nowrap">
                                        {line.rawValue !== null ? money(line.rawValue) : '—'}
                                    </td>
                                    <td className="px-4 py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                                        {figure(line.reportedValue, isTax)}
                                    </td>
                                    <td className="px-4 py-2 text-right text-gray-500 whitespace-nowrap">
                                        {line.monthlyFiledSum !== null ? money(line.monthlyFiledSum) : '—'}
                                    </td>
                                    <td className={`px-4 py-2 text-right whitespace-nowrap ${
                                        bridge(line) === '✓' ? 'text-green-600' : 'text-amber-700'}`}>
                                        {bridge(line)}
                                    </td>
                                    {isFiled && (
                                        <td className={`px-4 py-2 text-right whitespace-nowrap ${
                                            filedDiff !== null && Math.abs(filedDiff) >= 0.005
                                                ? 'font-medium text-amber-700' : 'text-gray-700'}`}>
                                            {line.filedValue !== null ? money(line.filedValue) : '—'}
                                            {filedDiff !== null && Math.abs(filedDiff) >= 0.005 && (
                                                <span className="ml-1 text-xs">({money(filedDiff)})</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-2 border-t text-xs text-gray-500">
                {t('annual.formLegend')}
            </div>
        </div>
    );
}
